import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { DateRange } from '@mui/lab/DateRangePicker';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import './Payment.scss';

import { formatAccountType, getSearchParamValue, getErrorMessage } from '../../utils';
import { AccountTypes, GlobalRouteNames, LocalStorageItems, QueryParams } from '../../models/Common';
import { Page404 } from '../404';
import { ApiError } from '../../models/ApiError';
import { OrderParams, PreparedCalculateData } from '../../models/Calculate';
import { paymentsApi } from '../../services/apiServices/user/payments';
import { initialState } from '../../store/reducers/UserInfoSlice';
import { useUserAddress } from '../../hooks/useUserAddress';
import { useAppSelector } from '../../hooks/redux';
import { useAccountInfo } from '../../hooks/useAccountInfo';
import { DateVOv2 } from '../../core/DateVOv2';
import { useCallMeBack } from '../../hooks/useCallMeBack';
import FormDateRangePicker from '../../components/inputs/FormDateRangePicker/FormDateRangePicker';
import ServiceInfo from '../../components/servicePaymentParts/ServiceInfo/ServiceInfo';
import InfoBlockPrepaidOrDaily from '../../components/servicePaymentParts/InfoBlock/InfoBlockPrepaid/InfoBlockPrepaidOrDaily';
import { UserSubtitle } from '../../components/commons/UserSubtitle';
import { IMultiAccount } from '../../models/UserInfo';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import ServicePaymentFormRefactor from '../../components/servicePaymentParts/ServicePaymentFormRefactor/ServicePaymentFormRefactor';

export interface ServicePaymentFormState extends OrderParams {
  activationPeriod: DateRange<DateTime>;
  activationDate: DateTime<boolean>;
}

export const DailyPayment = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const { callMeBack } = useCallMeBack();
  const controlledAccount = useMemo(() => {
    const acc = localStorage.getItem(LocalStorageItems.ControlledAccount);
    return acc ? JSON.parse(acc) : null;
  }, []) as IMultiAccount | null;
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const accountType = formatAccountType(getSearchParamValue(QueryParams.AccountType, search));
  const accountId = getSearchParamValue(QueryParams.AccountId, search);
  const serviceForPaymentId = +getSearchParamValue(QueryParams.ServiceId, search);
  const { accountInfo, isError: isAccountError, error: accountError } = useAccountInfo(accountId, accountType);
  const isChangeService = getSearchParamValue(QueryParams.IsChangeService, search) === 'true';
  const counts = +getSearchParamValue(QueryParams.Counts, search);
  const internetLogin =
    accountType === AccountTypes.Internet || accountType === AccountTypes.Union ? accountInfo.internetLogin : null;

  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: UserInfoState } = controlledAccount?.UserID
    ? useGetUserInfoMultiAccQuery({ subAccountId: controlledAccount?.UserID })
    : useGetUserInfoQuery('');
  const { Permissions, Shares: userInfoShares } = UserInfoState ?? {};
  const { userAddress } = useUserAddress(UserInfoState || initialState.userInfoState, selectedLanguage);

  const [calculateCostAPI, { isLoading, isError: calculateError }] =
    paymentsApi.useCalculateCostWithTransformResponseMutation();
  const {
    data: init,
    error: initError,
    isError: isInitError,
  } = paymentsApi.useGetServicePresetsWithTransformResponseQuery(
    { accountId, accountType, serviceForPaymentId },
    { skip: !accountType },
  );
  const { Services, FromDate, StatusID, Prepaids, serviceForPayment } = init ?? {};

  const methods = useForm<ServicePaymentFormState>({
    defaultValues: {
      accountType,
      accountId,
      serviceId: serviceForPayment?.id,
      ServiceExIDs: [],
      dateFrom: new DateVOv2().getLuxonKiev().toFormat('dd-MM-yyyy'),
      count: counts || 1,
      isBonusPayment: false,
      activationDate: new DateVOv2().getLuxonKiev(),
      activationPeriod: [null, null],
      changeCurrentService: isChangeService,
    },
  });

  const [calculation, setCalculation] = useState<null | PreparedCalculateData>(null);

  const updateCalculation = useCallback(async () => {
    const res = await calculateCostAPI(methods.getValues()).unwrap();
    setCalculation(res);
  }, [calculateCostAPI, methods]);

  useEffect(() => {
    if (Services && !serviceForPayment) {
      history.push(GlobalRouteNames.Page404);
    }
  }, [Services, history, serviceForPayment]);

  useEffect(() => {
    if ((accountError && isAccountError) || (initError && isInitError)) {
      const message = getErrorMessage(accountError || initError);
      history.replace({ pathname: GlobalRouteNames.UnknownError, state: { message } });
    }
  }, [accountError, isAccountError, initError, isInitError]);

  useEffect(() => {
    if (!FromDate || !serviceForPayment?.id) return;
    const dateFromPresets = new DateVOv2({ dateString: FromDate, format: 'yyyy-MM-dd HH:mm:ss' }).getLuxonKiev();
    const initialDate =
      dateFromPresets.startOf('day') < new DateVOv2().getLuxonKiev().startOf('day')
        ? new DateVOv2().getLuxonKiev()
        : dateFromPresets;
    methods.setValue('dateFrom', initialDate.toFormat('dd-MM-yyyy'));
    methods.setValue('activationDate', initialDate);
    methods.setValue('activationPeriod', [initialDate, initialDate]);
    methods.setValue('serviceId', serviceForPayment?.id);
    methods.setValue('changeCurrentService', isChangeService && StatusID === 1);
    updateCalculation();
  }, [FromDate, methods, serviceForPayment?.id, updateCalculation]);

  const onChangeUseBonusPoints = useCallback(
    (e: boolean) => {
      methods.setValue('isBonusPayment', e);
      updateCalculation();
    },
    [methods, updateCalculation],
  );

  const handleChangeDateRange = (dates: DateRange<DateTime>) => {
    if (dates[0] && dates[1]) {
      methods.setValue('dateFrom', dates[0].toFormat('dd-MM-yyyy'));
      methods.setValue('count', dates[1].diff(dates[0], 'days').days + 1);
    }
    if (dates[0] && !dates[1]) {
      methods.setValue('dateFrom', dates[0].toFormat('dd-MM-yyyy'));
      methods.setValue('count', 1);
    }
    updateCalculation();
  };

  return (
    <Switch>
      <Route path={GlobalRouteNames.DailyServicePayment} exact>
        <div className="page payment">
          <div className="payment__container container">
            <div className="payment__card card card__primary card__primary_full-mobile">
              {!controlledAccount ? (
                <h1 className="payment__title">{t('servicePayment.serviceActivation')}</h1>
              ) : (
                <h1 className="payment__title">
                  {t('servicePayment.serviceAccountActivation')}
                  <UserSubtitle address={userAddress.trim()} userId={controlledAccount.UserID} />
                </h1>
              )}
              <div className="payment__content">
                <FormProvider {...methods}>
                  <div className="payment__content__options">
                    <div className="payment__content__options__service-changer">
                      <ServiceInfo
                        internetLogin={internetLogin}
                        service={serviceForPayment}
                        userInfoShares={userInfoShares}
                      />
                    </div>
                    <div className="payment__content__options__activation-period">
                      <FormDateRangePicker
                        minDate={methods.getValues().activationDate as DateTime}
                        handleChange={handleChangeDateRange}
                      />

                      {isChangeService && (StatusID === 1 || Prepaids?.length) ? (
                        <InfoBlockPrepaidOrDaily
                          handleCallback={callMeBack}
                          infoText={t('servicePayment.infoBlockDaily')}
                        />
                      ) : null}
                    </div>
                  </div>
                  <ServicePaymentFormRefactor
                    calculation={calculation}
                    isLoading={isLoading}
                    onChangeUseBonusPoints={onChangeUseBonusPoints}
                    isShowBonusCheckbox
                    params={methods.watch()}
                    init={init}
                    accountType={accountType}
                    Permissions={Permissions}
                    typeInfoBlock={null}
                    calculateError={calculateError as unknown as ApiError}
                  />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </Route>
      <Route component={Page404} path={GlobalRouteNames.Page404} />
    </Switch>
  );
};
